import { markRaw } from 'vue'
import { Box, ContentData } from '@rtl/api'
import { cloneDeep } from 'lodash'
import toSource from 'tosource'

const EOL = '\r\n'

export type SkeletonItem = Omit<Box, 'containedBoxes'> & { containedBoxes?: Array<SkeletonItem> } | string

export function camelToKebab (str: string): string {
  return str.charAt(0).toLowerCase() + str.slice(1).replace(/[A-Z]/g, s => '-' + s.toLowerCase())
}

export function kebabToCamel (str: string): string {
  return str.replace(/-([a-z])/g, g => g[1].toUpperCase())
}

export function toValue (val: string): unknown {
  return Function(`"use strict";return (${val})`)() // eslint-disable-line no-new-func
}

export function stripHtml (str?: string) {
  return str?.replace(/<[^>]*>?/gm, '').trim()
}

export function parseHtmlString (src: string): Array<Node> {
  return Array.from(findElement(parseFromString(`<body>${src}</body>`), 'body')?.childNodes ?? [])
}

export function parseFromString (src: string): Array<Node> {
  let children: Array<Node> = []

  const parse = () => {
    if (typeof window !== 'undefined' && window.DOMParser) {
      const domParser = new window.DOMParser()
      children = markRaw(Array.from(domParser.parseFromString(src, 'text/html').documentElement.childNodes))
    } else {
      const { JSDOM } = require('jsdom')
      const dom = new JSDOM(src)
      children = markRaw(Array.from(dom.window.document.childNodes))
      //dom.window.close()
    }
  }

  parse()

  return children
}

export function renderHtmlString (tag: string, attrs: Record<string, unknown> = {}, content = '') {
  const options = {
    indent: '  ',
    eol: EOL,
    attrsFn (attrs: Record<string, unknown>) {
      return Object.keys(attrs).map(key => {
        const name = camelToKebab(key)
        const value = key.startsWith(':')
          ? toSource(attrs[key], undefined, false).replaceAll("'", "\\'" ).replaceAll('"', "'")
          : attrs[key]

        return value === undefined || value === null ? name : `${name}="${value}"`
      })
    }
  }

  const open = `<${[tag, ...options.attrsFn(attrs)].join(' ')}>`
  const close = `</${tag}>`
  const children = content ? options.eol + content.replace(/^./gm, s => options.indent + s) + options.eol : ''

  return open + children + close
}

export function timingFilter (items: Array<SkeletonItem>) {
  return cloneDeep(items).reduce<Array<SkeletonItem>>((acc, item) => {
    if (typeof item === 'string') {
      acc.push(item)
    } else {
      const now = new Date()
      const timings: Array<{ day: number, from: string, until: string }> | null = item.parameters['timingType'] === 'recurrence' ? item.parameters[':timingRecords'] ?? [] : null
      
      const enabled = timings
        ? timings.some(timing => timing.day === now.getDay() - 1 && new Date(now.toDateString() + ' ' + timing.from + ':00').getTime() <= now.getTime() && new Date(now.toDateString() + ' ' + timing.until + ':59').getTime() >= now.getTime())
        : (!item.timeFrom || new Date(item.timeFrom).getTime() < now.getTime()) && (!item.timeUntil || new Date(item.timeUntil).getTime() > now.getTime())

      if (enabled) {
        if (item.containedBoxes) {
          item.containedBoxes = timingFilter(item.containedBoxes)
        }
        acc.push(item)
      }
    }

    return acc
  }, [])
}

export function htmlSerializer (items: Array<SkeletonItem> = [], timing = true): string {
  const serialize = (items: Array<SkeletonItem>): string => items.map(item => {
    if (typeof item === 'string') {
      return item
    }

    const attrs: Record<string, unknown> = {}
    const params = item.parameters

    if (item.timeFrom) {
      const timeFrom = new Date(item.timeFrom)
      if (timeFrom.getUTCFullYear() > 2001) {
        params.timeFrom = timeFrom.toISOString()
      }
    }
    if (item.timeUntil) {
      const timeUntil = new Date(item.timeUntil)
      if (timeUntil.getUTCFullYear() < 2099) {
        params.timeUntil = timeUntil.toISOString()
      }
    }

    for (const key in params) {
      if (params[key] !== undefined) {
        attrs[key] = params[key]
      }
    }

    const children = item.containedBoxes ? serialize(item.containedBoxes) : ''

    return renderHtmlString(item.componentName, attrs, children)
  }).join(EOL)

  const src = timing ? timingFilter(cloneDeep(items)) : cloneDeep(items)

  return serialize(src)
}

export function htmlToObject (nodes: Array<Node>) {
  const items: Array<SkeletonItem> = []

  nodes.forEach(node => {
    if (node.nodeName === '#text') {
      const item = (node.textContent as string).trim()
      if (item) {
        items.push(item)
      }
    } else {
      const element = node as Element

      const attrs = Object.fromEntries(Array.from(element.attributes).map(attr => {
        const key = kebabToCamel(attr.name)
        const value = (attr.name.charAt(0) === ':' ? toValue(attr.value) : attr.value)
        return [key, value]
      }))

      const { timeFrom, timeUntil, ...parameters } = attrs

      const item: SkeletonItem = {
        id: -1,
        componentName: element.tagName.toLowerCase(),
        parameters,
        timeFrom: timeFrom ? new Date(timeFrom as string) : undefined,
        timeUntil: timeUntil ? new Date(timeUntil as string) : undefined,
        containedBoxes: htmlToObject(Array.from(element.childNodes))
      }

      items.push(item)
    }
  })

  return items
}

export const decorateWithBanners = (cb: (index: number) => SkeletonItem, skeleton?: Array<SkeletonItem>, n = 1) => {
  let index = 0
  return skeleton?.reduce<Array<SkeletonItem>>((res, item, i) => {
    res.push(item)
    if ((i + 1) % n === 0) {
      res.push(cb(index))
      index += 1
    }
    return res
  }, []) ?? []
}

export function renderContentWidgets (widgets: ContentData['widgets'], targetHtml: string): string {
  for (const widgetId in widgets) {
    const widget = widgets[widgetId]
    const html = htmlSerializer([widget])
    targetHtml = targetHtml.replace(`<span data-widget-id="${widgetId}"></span>`, html)
  }

  return targetHtml
}

export function findElement (nodes: Array<Node>, tagName: string): HTMLElement | undefined {
  const found = nodes.find(node => (node as any).tagName?.toLowerCase() === tagName.toLowerCase()) as HTMLElement | undefined

  if (found) {
    return found
  }

  for (const node of nodes) {
    if ('childNodes' in node) {
      const found = findElement(Array.from(node.childNodes), tagName)
      if (found) {
        return found
      }
    }
  }

  return
}

export function findElements (nodes: Array<Node>, tagName: string): Array<HTMLElement> {
  const found = nodes.filter(node => (node as any).tagName?.toLowerCase() === tagName.toLowerCase()) as Array<HTMLElement>

  for (const node of nodes) {
    if ('childNodes' in node) {
      found.push(...findElements(Array.from(node.childNodes), tagName))
    }
  }

  return found
}

export function getInnerHtml (el?: HTMLElement) {
  return el?.innerHTML ?? ''
}
